import { ChartOptions, createChart, DeepPartial, IChartApi, ISeriesApi, LineData, LineStyle, LineType, MouseEventParams, Time } from 'lightweight-charts'
import { PayoffStrikeData } from '../../../reducer/strategyBuilder.reducer'
import { PayoffLeg, PayoffProfitData, SdData } from '../../payoffContainer/livePayoffContainer'
import { PayoffChartCallbacks, PayoffChartDisplayOptions, PayoffChartOptions } from './payoffChart.typings'
import {
    formatPayoffOiScalePrice,
    formatPayoffPnlScalePrice,
    getDefaultDisplayOptions,
    payoffOiPriceFormat,
    payoffPnlPriceFormat
} from '../../../util/payoffChart'
import { green, red } from '../../../assets/colors'
import { OptionType, Security } from '../../../proto/generated/security'
import { FillSeries } from '../../chartV2/customSeries/fillSeries/fill-series'
import { greenHalf, redHalf } from '../../../pages/home/plChart'
import { FillSeriesOptions } from '../../chartV2/customSeries/fillSeries/options'
import { FillData } from '../../chartV2/customSeries/fillSeries/data'
import { VerticalLines } from '../../chartV2/customSeries/verticalLine/vertical-lines'
import { VertLineOptions } from '../../chartV2/customSeries/verticalLine/options'
import { VerticalLineData } from '../../chartV2/customSeries/verticalLine/data'

import { StackedBarsSeries } from '../../chartV2/customSeries/stackedBarSeries/stacked-bars-series'
import { StackedBarsSeriesOptions } from '../../chartV2/customSeries/stackedBarSeries/options'
import { GROUPED_BAR_WIDTH } from '../../payoffChart/PayoffChart'
import { StackedBarsData } from '../../chartV2/customSeries/stackedBarSeries/data'
import LabelledVerticalLineDrawing, { LabelledVerticalLineDrawingOptions } from './labelledVerticalLineDrawing/labelledVerticalLine.drawing'
import { callColorCumulative, putColorCumulative } from '../../multiStikeChart/styles'
import PlusIconDrawing from './plusIconDrawing/plusIcon.drawing'
import { SQUARE_WIDTH } from './plusIconDrawing/plusIcon.paneRenderer'
import { isEmbeddedModeSet } from '../../../util/general'
import { Theme } from '@mui/material'
import { getOverlayPriceScaleStyleOptions, OverlayPriceScale } from '../../chartV2/chartWrapper/overlay-price-scale.ts'
import {
    CHART_PLUS_BUTTON_BACKGROUND,
    CHART_PLUS_BUTTON_BACKGROUND_HOVER
} from '../../chartV2/chartContextMenuButton/chartPlusButtonColors'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer.ts'
import store from '../../../store.ts'
import { FeatureCode } from '../../../util/RestrictionFeatureCode.ts'

const EXPECTED_PROFIT_SERIES = 'EXPECTED_PROFIT_SERIES'
const EXPIRY_LOSS_FILL_SERIES = 'EXPIRY_LOSS_FILL_SERIES'
const EXPIRY_GAIN_FILL_SERIES = 'EXPIRY_GAIN_FILL_SERIES'
const EXPIRY_PROFIT_SERIES = 'EXPIRY_PROFIT_SERIES'
const OI_BARS_SERIES = 'OI_BARS_SERIES'
const EXISTING_PROFIT_SERIES = 'EXISTING_PROFIT_SERIES'

export default class PayoffChart {
    underlying: Security
    container: HTMLDivElement
    orderSlidersElements: Record<string, HTMLDivElement>
    payoffLegs: PayoffLeg[]
    breakevenPoints: number[]
    payoffLegsPositions: Record<string, number> = {}
    profitData: PayoffProfitData[]
    sdData: SdData[]
    spotPrice: number
    payoffStrikes: PayoffStrikeData[]
    displayOptions: PayoffChartDisplayOptions
    layoutOptions: DeepPartial<ChartOptions>
    theme: Theme
    isEmbedded: boolean
    chartApi: IChartApi
    seriesApiMap: Record<string, ISeriesApi<any>> = {}
    expectedProfitSeriesData: LineData[] = []
    expiryProfitSeriesData: LineData[] = []
    expiryProfitGainSeriesData: FillData[] = []
    expiryProfitLossSeriesData: FillData[] = []
    existingProfitSeriesData: LineData[] = []
    sdLinesSeriesData: VerticalLineData[] = []
    oiBarsData: StackedBarsData[] = []
    oiBarWidth: number = GROUPED_BAR_WIDTH
    sdLines: LabelledVerticalLineDrawing[] = []
    payoffLegLines: Record<string, LabelledVerticalLineDrawing> = {}
    plusIconDrawing: PlusIconDrawing
    hoveredTime?: Time = null
    strikeToLegsMap: Record<number, string[]> = {}
    dragStartX = 0
    addOrderEnabled = true
    draggingLegId = null
    /** Strike snapped on drop, until payoffLegs props reflect the update */
    pendingLegStrikes: Record<string, number> = {}
    hoveredStrike = null
    interactionsEnabled: boolean = false
    callbacks: PayoffChartCallbacks = {
        updateLeg(legId, security) {},
        onAddLegClick(clickInfo) {},
        onCrosshairMove(nearestStrike, time) {}
    }

    constructor({
        container,
        orderSlidersElements,
        underlying,
        payoffLegs,
        profitData,
        isEmbedded,
        breakevenPoints,
        sdData,
        spotPrice,
        payoffStrikes,
        displayOptions = getDefaultDisplayOptions(),
        layoutOptions,
        callbacks,
        theme,
        interactionsEnabled
    }: PayoffChartOptions) {
        this.container = container
        this.orderSlidersElements = orderSlidersElements
        this.underlying = underlying
        this.payoffLegs = payoffLegs
        this.breakevenPoints = breakevenPoints
        this.profitData = profitData
        this.sdData = sdData
        this.spotPrice = spotPrice
        this.payoffStrikes = payoffStrikes
        this.displayOptions = displayOptions
        this.layoutOptions = layoutOptions
        this.interactionsEnabled = interactionsEnabled
        this.theme = theme

        if (callbacks) {
            for (let key in callbacks) {
                this.callbacks[key] = callbacks[key]
            }
        }

        this.chartApi = createChart(this.container, {
            ...this.layoutOptions,
            autoSize: true,
            height: this.container.clientHeight,
            width: this.container.clientWidth
        })
        this.isEmbedded = isEmbedded
        this.createProfitSeriesData()
        this.createOiSeriesData()
        this.updatePayoffLegsPositions()
        this.subscribeEventListeners()
        this.initialize()
    }

    initialize() {
        this.chartApi.applyOptions({
            leftPriceScale: {
                borderVisible: false,
                autoScale: true
            },
            rightPriceScale: {
                borderVisible: false,
                autoScale: true
            }
        })
        this.initializeExpiryProfitSeries()
        this.initializeExpectedProfitSeries()
        this.initializeExistingProfitSeries()
        this.createOrUpdateSdLines()
        this.createOrUpdatePayoffLegLines()
        this.initializeOiBars()
        this.resetViewport()
        this.addPlusIcon()
    }

    setDisplayOptions(options: Partial<PayoffChartDisplayOptions>) {
        this.displayOptions = { ...this.displayOptions, ...options }
        if (!this.displayOptions.showOI) {
            this.seriesApiMap[OI_BARS_SERIES].applyOptions({ visible: false })
        } else {
            this.seriesApiMap[OI_BARS_SERIES].applyOptions({ visible: true })
        }
        this.createOrUpdateSdLines()
        if(this.displayOptions.showExistingProfitSeries) {
            if(!this.seriesApiMap[EXISTING_PROFIT_SERIES]) {
                this.initializeExistingProfitSeries()
            } else {
                this.seriesApiMap[EXISTING_PROFIT_SERIES].applyOptions({ visible: true })
            }
        } else {
            if(this.seriesApiMap[EXISTING_PROFIT_SERIES]) {
                this.seriesApiMap[EXISTING_PROFIT_SERIES].applyOptions({ visible: false })
            }
        }
    }

    addPlusIcon() {
        this.plusIconDrawing = new PlusIconDrawing({
            price: 0 as Time,
            fillColor: CHART_PLUS_BUTTON_BACKGROUND,
            hoverFillColor: CHART_PLUS_BUTTON_BACKGROUND_HOVER
        })
        this.seriesApiMap[EXPIRY_PROFIT_SERIES].attachPrimitive(this.plusIconDrawing)
    }

    getPlusIconAnchorPosition(time: Time): { x: number; y: number } | null {
        const xCoordinate = this.chartApi.timeScale().timeToCoordinate(time)
        if (xCoordinate === null) return null
        const leftScaleWidth = this.getPriceScaleWidth('left')
        const timeScaleHeight = this.chartApi.timeScale().height()
        const chartHeight = this.container.clientHeight
        return {
            x: xCoordinate - SQUARE_WIDTH / 2 + leftScaleWidth,
            y: chartHeight - timeScaleHeight - SQUARE_WIDTH
        }
    }

    createOrUpdatePayoffLegLines() {
        if (!this.chartApi) return
        const leftScaleWidth = this.chartApi.priceScale('left').width()
        const leftScaleVisible = this.chartApi.priceScale('left').options().visible
        const updatedLegs: string[] = []
        this.payoffLegs.forEach((leg) => {
            updatedLegs.push(leg.id)
            let drawing = this.payoffLegLines[leg.id]
            let position = this.payoffLegsPositions[leg.id]
            if (position === undefined) return
            if (leftScaleVisible) {
                position -= leftScaleWidth
            }
            const time = this.chartApi.timeScale().coordinateToTime(position)
            if (!time) return
            const options: LabelledVerticalLineDrawingOptions = {
                price: time,
                showLabel: false,
                showAxisLabel: true,
                label: this.getLegAxisLabel(leg),
                axisLabelColor: leg.security.optionType === OptionType.CE ? callColorCumulative : putColorCumulative,
                axisLabelTextColor: leg.security.optionType === OptionType.CE ? 'white' : 'black',
                lineType: LineStyle.Dashed,
                color: leg.quantity > 0 ? this.theme.palette.buy.main : this.theme.palette.sell.main,
                opacity: 1,
                lineVisible: this.draggingLegId === leg.id
            }
            if (!drawing) {
                drawing = new LabelledVerticalLineDrawing(options)
                this.payoffLegLines[leg.id] = drawing
                this.seriesApiMap[EXPIRY_PROFIT_SERIES].attachPrimitive(drawing)
            } else {
                drawing.setOptions(options)
            }
        })

        Object.keys(this.payoffLegLines).forEach((legId) => {
            if (!updatedLegs.includes(legId)) {
                const drawing = this.payoffLegLines[legId]
                this.seriesApiMap[EXPIRY_PROFIT_SERIES].detachPrimitive(drawing)
                drawing.destroy()
                delete this.payoffLegLines[legId]
            }
        })
    }

    updatePayoffLegs(payoffLegs: PayoffLeg[]) {
        this.payoffLegs = payoffLegs
        this.updatePayoffLegsPositions()
        this.createOrUpdatePayoffLegLines()
        this.resetPaneView()
    }

    updateOrderSliderElements(elements: Record<string, HTMLDivElement>) {
        this.orderSlidersElements = elements
        this.updatePayoffLegsPositions()
        this.createOrUpdatePayoffLegLines()
    }
    getLegStrike(leg: PayoffLeg): number {
        return (leg.security.strikePrice ?? 0) / (leg.security.priceMultiplier ?? 100)
    }

    getLegDisplayStrike(leg: PayoffLeg): number {
        return this.pendingLegStrikes[leg.id] ?? this.getLegStrike(leg)
    }

    getLegAxisLabel(leg: PayoffLeg): string {
        if (this.draggingLegId === leg.id) {
            const strike = this.hoveredStrike ?? this.getLegDisplayStrike(leg)
            return String(strike)
        }
        return String(this.getLegDisplayStrike(leg))
    }

    updatePayoffLegsPositions = () => {
        if (!this.chartApi) return
        const timescale = this.chartApi.timeScale()
        if (!timescale.getVisibleRange()) {
            setTimeout(this.updatePayoffLegsPositions, 50)
        } else {
            const activeLegIds = new Set(this.payoffLegs.map((leg) => leg.id))
            Object.keys(this.pendingLegStrikes).forEach((legId) => {
                if (!activeLegIds.has(legId)) {
                    delete this.pendingLegStrikes[legId]
                }
            })

            const strikeToLegsMap: Record<number, string[]> = {}
            const positions: Record<string, number> = this.payoffLegs.reduce(
                (acc, leg) => {
                    const security = leg.security
                    const legStrike = this.getLegStrike(leg)
                    const strike = this.getLegDisplayStrike(leg)
                    if (this.pendingLegStrikes[leg.id] !== undefined && legStrike === this.pendingLegStrikes[leg.id]) {
                        delete this.pendingLegStrikes[leg.id]
                    }
                    const xCoordinate = this.chartApi.timeScale().timeToCoordinate(strike as Time)
                    const leftScaleWidth = this.chartApi.priceScale('left').width()
                    const leftScaleVisible = this.chartApi.priceScale('left').options().visible

                    if (!xCoordinate) return acc
                    if (!strikeToLegsMap[strike]) {
                        strikeToLegsMap[strike] = []
                    }
                    strikeToLegsMap[strike].push(leg.id)
                    return {
                        ...acc,
                        [leg.id]: xCoordinate + (leftScaleVisible ? leftScaleWidth : 0)
                    }
                },
                {} as Record<string, number>
            )

            this.payoffLegsPositions = positions
            this.strikeToLegsMap = strikeToLegsMap

            requestAnimationFrame(() => {
                for (let strike in this.strikeToLegsMap) {
                    const legs = this.strikeToLegsMap[strike]
                    legs.forEach((id, i) => {
                        if (this.orderSlidersElements[id]) {
                            this.orderSlidersElements[id].style.left = `${positions[id] + i * 3}px`
                            this.orderSlidersElements[id].style.top = `${i * 5}px`
                        }
                    })
                }
            })
        }
    }

    setAddOrderEnabled(enabled: boolean) {
        this.addOrderEnabled = enabled
        this.plusIconDrawing?.setOptions({
            visible: this.addOrderEnabled
        })
    }

    resetViewport = () => {
        if (this.profitData.length < 2) return
        const timescale = this.chartApi.timeScale()
        const chartWidth = timescale.width()
        if (!chartWidth) {
            setTimeout(this.resetViewport, 50)
            return
        }

        const barSpacing = isEmbeddedModeSet() ? 2 : 0.8
        const minNumberOfBars = Math.floor(chartWidth / barSpacing)

        const startPrice = this.profitData[0].price || 0
        let midPoint = Math.ceil((this.profitData.length - 1) / 2) || 0
        let from = midPoint - Math.ceil(minNumberOfBars / 2)
        let to = midPoint + Math.ceil(minNumberOfBars / 2)

        const firstBreakevenPoint = this.breakevenPoints[0]
        const lastBreakevenPoint = this.breakevenPoints[this.breakevenPoints.length - 1]

        if (firstBreakevenPoint && lastBreakevenPoint) {
            let startPoint = firstBreakevenPoint
            let endPoint = lastBreakevenPoint
            const numberOfCenterBars = lastBreakevenPoint - firstBreakevenPoint
            const remainingBars = minNumberOfBars - numberOfCenterBars
            if (remainingBars > 0) {
                startPoint -= Math.floor(remainingBars / 2)
                endPoint += Math.ceil(remainingBars / 2)
            } else {
                startPoint -= Math.floor(numberOfCenterBars / 5)
                endPoint += Math.ceil(numberOfCenterBars / 5)
            }

            from = startPoint - startPrice
            to = endPoint - startPrice
        }
        timescale.setVisibleLogicalRange({ from, to })
    }

    updateProfitData(profitData: PayoffProfitData[], breakevenPoints: number[]) {
        this.profitData = profitData
        this.breakevenPoints = breakevenPoints
        this.createProfitSeriesData()
        this.setProfitSeriesData()
    }

    updatePayoffStrikes(payoffStrikes: PayoffStrikeData[]) {
        this.payoffStrikes = payoffStrikes
        this.createOiSeriesData()
        this.setOiSeriesData()
    }
    updateSdLines(sdData: SdData[], spotPrice: number) {
        this.sdData = sdData
        this.spotPrice = spotPrice
        this.createOrUpdateSdLines()
    }

    setProfitSeriesData() {
        try {
            this.seriesApiMap[EXPIRY_PROFIT_SERIES]?.setData(this.expiryProfitSeriesData)
            this.seriesApiMap[EXPIRY_LOSS_FILL_SERIES]?.setData(this.expiryProfitLossSeriesData)
            this.seriesApiMap[EXPIRY_GAIN_FILL_SERIES]?.setData(this.expiryProfitGainSeriesData)
            this.seriesApiMap[EXPECTED_PROFIT_SERIES]?.setData(this.expectedProfitSeriesData)
            this.seriesApiMap[EXISTING_PROFIT_SERIES]?.setData(this.existingProfitSeriesData)
        } catch (e) {}
    }

    setOiSeriesData() {
        try {
            this.seriesApiMap[OI_BARS_SERIES]?.setData(this.oiBarsData)
            let minTimeDiff = this.payoffStrikes.length > 1 ? this.payoffStrikes[1].strike - this.payoffStrikes[0].strike : 1
            for (let i = 1; i < this.payoffStrikes.length - 1; i++) {
                const timeDiff = this.payoffStrikes[i].strike - this.payoffStrikes[i - 1].strike
                if (Math.abs(timeDiff) < minTimeDiff) {
                    minTimeDiff = timeDiff
                }
            }
            this.oiBarWidth = minTimeDiff * window.devicePixelRatio * 0.5
            this.seriesApiMap[OI_BARS_SERIES].applyOptions({ barWidth: this.oiBarWidth })
            this.chartApi.priceScale('left').applyOptions({ autoScale: true })
        } catch (e) {}
    }

    resetPaneView() {
        if (!this.payoffStrikes?.length) return
        const oneSd = this.sdData?.find((d) => d.label === '1 SD')
        const spotPrice = this.spotPrice / (this.underlying.priceMultiplier ?? 100)
        const spotPriceNearest = this.getNearestStrike(spotPrice as number)
        let spotPriceNearestIndex = this.payoffStrikes.findIndex((s) => s.strike === spotPriceNearest)
        if (spotPriceNearestIndex === -1) spotPriceNearestIndex = this.payoffStrikes.length / 2
        if (oneSd && spotPrice) {
            const oneSdWidth = Math.floor(Math.abs(spotPrice - oneSd.price))
            const threeSd = this.getNearestStrike((spotPrice + 3 * oneSdWidth) as number)
            const minusThreeSd = this.getNearestStrike((spotPrice - 3 * oneSdWidth) as number)
            const orderStrikes = Object.keys(this.strikeToLegsMap).map((d) => Number(d))
            const minOrderStrike = Math.min(...orderStrikes)
            const maxOrderStrike = Math.max(...orderStrikes)
            let minIndex = this.payoffStrikes.findIndex((s) => s.strike === minOrderStrike)
            let maxIndex = this.payoffStrikes.findIndex((s) => s.strike === maxOrderStrike)
            minIndex = Math.max(minIndex - 2, 0)
            maxIndex = Math.min(maxIndex + 2, this.payoffStrikes.length - 1)
            const viewObj = {
                from: Math.min(minusThreeSd, this.payoffStrikes[minIndex].strike, this.payoffStrikes[Math.max(0, spotPriceNearestIndex - 10)].strike) as Time,
                to: Math.max(threeSd, this.payoffStrikes[maxIndex].strike, this.payoffStrikes[Math.min(spotPriceNearestIndex + 10, this.payoffStrikes.length - 1)].strike) as Time
            }
            setTimeout(() => {
                this.chartApi?.timeScale()?.setVisibleRange(viewObj)
            }, 0)
        }
    }

    subscribeEventListeners() {
        this.chartApi.timeScale().subscribeVisibleTimeRangeChange(this.onVisibleTimeRangeChange)
        this.chartApi.subscribeCrosshairMove(this.onCrossHairMove)
        this.chartApi.subscribeClick(this.onClick)
    }

    getPriceScaleWidth(priceScaleId: 'left' | 'right') {
        const scale = this.chartApi.priceScale(priceScaleId)
        if (!scale.options().visible) return 0
        return scale.width()
    }

    onClick = (event: MouseEventParams<Time>) => {
        const { time, hoveredObjectId, point } = event
        const nearestStrike = this.getNearestStrike(time as number)

        if (this.addOrderEnabled && hoveredObjectId === 'plusIcon') {
            const anchor = this.getPlusIconAnchorPosition(nearestStrike as Time)
            if (anchor) {
                this.callbacks.onAddLegClick?.({
                    strike: nearestStrike * (this.underlying.priceMultiplier ?? 100),
                    x: anchor.x,
                    y: anchor.y
                })
            }
        }
    }

    onCrossHairMove = (event: MouseEventParams<Time>) => {
        const { time, hoveredObjectId } = event
        const nearestStrike = this.getNearestStrike(time as number)
        this.updateHoveredBar(time, nearestStrike)
        this.plusIconDrawing?.setOptions({
            price: time || (0 as Time),
            hovered: this.addOrderEnabled && hoveredObjectId === 'plusIcon'
        })
        this.callbacks.onCrosshairMove && this.callbacks.onCrosshairMove(nearestStrike, time)
    }

    onVisibleTimeRangeChange = () => {
        this.updatePayoffLegsPositions()
        this.createOrUpdatePayoffLegLines()
    }

    updateHoveredBar(time: Time, nearestStrike: number) {
        const nearestCoordinate = this.chartApi.timeScale().timeToCoordinate(nearestStrike as Time)

        if (!nearestCoordinate) {
            this.hoveredTime && this.setHoveredBar(0 as Time)
            return
        }
        // const dpr = window.devicePixelRatio;

        // const barWidth = this.chartApi.timeScale().options().barSpacing * this.oiBarWidth;
        // const singleBarWidth = barWidth / (2 + 1) // 2 is the number of single bars
        // const leftBound = nearestCoordinate * dpr - (!!this.hoveredTime ? 1.5 * singleBarWidth : singleBarWidth)
        // const rightBound = nearestCoordinate * dpr + (this.hoveredTime ? 1.5 * singleBarWidth : singleBarWidth)

        // const sx = time ? this.chartApi.timeScale().timeToCoordinate(time)*dpr : null;
        // if (!sx || sx < leftBound || sx > rightBound) {
        //     this.hoveredTime && this.setHoveredBar(0 as Time);
        //     return
        // }

        if (this.hoveredTime !== nearestStrike) {
            this.setHoveredBar(nearestStrike as Time)
        }
    }

    setHoveredBar(time: Time) {
        const options: DeepPartial<StackedBarsSeriesOptions> = {
            hoveredTime: time
        }
        this.hoveredTime = time
        this.seriesApiMap[OI_BARS_SERIES].applyOptions(options)
    }

    initializeExpiryProfitSeries() {
        const api = this.chartApi.addLineSeries({
            color: 'black',
            priceScaleId: 'right',
            lineWidth: 1,
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: payoffPnlPriceFormat
        })
        this.seriesApiMap[EXPIRY_PROFIT_SERIES] = api

        if (this.isEmbedded) {
            api.attachPrimitive(
                new OverlayPriceScale({
                    side: 'right',
                    formatPrice: formatPayoffPnlScalePrice,
                    ...getOverlayPriceScaleStyleOptions(this.theme)
                })
            )
        }

        const lossApi = this.chartApi.addCustomSeries(
            new FillSeries() as any,
            {
                color: redHalf,
                priceScaleId: 'right',
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            } as FillSeriesOptions
        )

        this.seriesApiMap[EXPIRY_LOSS_FILL_SERIES] = lossApi

        const gainApi = this.chartApi.addCustomSeries(
            new FillSeries() as any,
            {
                color: greenHalf,
                priceScaleId: 'right',
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            } as FillSeriesOptions
        )

        this.seriesApiMap[EXPIRY_GAIN_FILL_SERIES] = gainApi
        this.setProfitSeriesData()
    }

    initializeExpectedProfitSeries() {
        const api = this.chartApi.addLineSeries({
            priceScaleId: 'right',
            lineWidth: 1,
            lineStyle: LineStyle.Solid,
            lineType: LineType.Curved,
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: payoffPnlPriceFormat
        })

        this.seriesApiMap[EXPECTED_PROFIT_SERIES] = api
        api.setData(this.expectedProfitSeriesData)
    }

    initializeExistingProfitSeries() {
        if(this?.displayOptions?.showExistingProfitSeries) {
            const api = this.chartApi.addLineSeries({
                color: 'gray',
                priceScaleId: 'right',
                lineWidth: 1,
                lineStyle: LineStyle.Dashed,
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            })
            this.seriesApiMap[EXISTING_PROFIT_SERIES] = api
            this.setProfitSeriesData()
        }
    }

    initializeOiBars() {
        const oiBarsApi = this.chartApi.addCustomSeries(
            new StackedBarsSeries() as any,
            {
                color: 'black',
                lastValueVisible: false,
                priceLineVisible: false,
                barWidth: this.oiBarWidth,
                disablePadding: true,
                increaseWidthOnHover: true,
                visible: this.displayOptions.showOI,
                chartApi: this.chartApi,
                priceScaleId: 'left',
                priceFormat: payoffOiPriceFormat
            } as StackedBarsSeriesOptions
        )

        this.seriesApiMap[OI_BARS_SERIES] = oiBarsApi

        if (this.isEmbedded) {
            oiBarsApi.attachPrimitive(
                new OverlayPriceScale({
                    side: 'left',
                    formatPrice: formatPayoffOiScalePrice,
                    ...getOverlayPriceScaleStyleOptions(this.theme)
                })
            )
        }

        this.setOiSeriesData()
    }

    createOrUpdateSdLines = () => {
        this.sdLines.forEach((drawing) => {
            this.seriesApiMap[EXPIRY_PROFIT_SERIES].detachPrimitive(drawing)
            drawing.destroy()
        })

        this.sdLines = []
        if (this.spotPrice) {
            const drawing = new LabelledVerticalLineDrawing({
                price: Math.floor(this.spotPrice / (this.underlying.priceMultiplier ?? 100)) as Time,
                label: 'Spot',
                lineType: LineStyle.Solid,
                color: 'rgb(136, 136,136)',
                opacity: 0.3
            })
            this.seriesApiMap[EXPIRY_PROFIT_SERIES].attachPrimitive(drawing)
            this.sdLines.push(drawing)
        }
        if (this.displayOptions.showSdLines) {
            this.sdData.forEach((sd) => {
                const drawing = new LabelledVerticalLineDrawing({
                    price: sd.price as Time,
                    label: sd.label,
                    lineType: LineStyle.Solid,
                    color: 'rgb(136, 136,136)',
                    opacity: 0.3
                })
                this.seriesApiMap[EXPIRY_PROFIT_SERIES].attachPrimitive(drawing)
                this.sdLines.push(drawing)
            })
            const api = this.chartApi.addCustomSeries(
                new VerticalLines() as any,
                {
                    color: 'green',
                    labelText: '',
                    width: 1,
                    labelBackgroundColor: 'green',
                    labelTextColor: 'white',
                    showLabel: false,
                    lineType: 'simple',
                    lastValueVisible: false,
                    priceLineVisible: false,
                    priceScaleId: 'right'
                } as VertLineOptions
            )

            this.seriesApiMap['SD_LINES'] = api
        }
        this.resetPaneView()
    }

    createProfitSeriesData() {
        const expectedProfitSeriesData: LineData[] = []
        const expiryProfitSeriesData: LineData[] = []
        const existingProfitSeriesData: LineData[] = []
        const expiryProfitGainSeriesData: FillData[] = []
        const expiryProfitLossSeriesData: FillData[] = []

        this.profitData.forEach((p) => {
            const strike = p.price
            expectedProfitSeriesData.push({
                value: p.expectedProfit / (this.underlying.priceMultiplier ?? 100),
                time: strike as Time
            })

            expiryProfitSeriesData.push({
                value: p.expiryProfit / (this.underlying.priceMultiplier ?? 100),
                time: strike as Time,
                color: p.expiryProfit > 0 ? green : red
            })
            expiryProfitGainSeriesData.push({
                time: strike as Time,
                low: 0,
                high: Math.max(0, p.expiryProfit / (this.underlying.priceMultiplier ?? 100))
            })

            expiryProfitLossSeriesData.push({
                time: strike as Time,
                high: 0,
                low: Math.min(0, p.expiryProfit / (this.underlying.priceMultiplier ?? 100))
            })

            if (this.displayOptions.showExistingProfitSeries) {
                existingProfitSeriesData.push({
                    value: p.existingExpiryProfit / (this.underlying.priceMultiplier ?? 100),
                    time: strike as Time,
                    color: p.existingExpiryProfit > 0 ? green : red
                })
            }
        })

        this.expectedProfitSeriesData = expectedProfitSeriesData
        this.expiryProfitSeriesData = expiryProfitSeriesData
        this.existingProfitSeriesData = existingProfitSeriesData
        this.expiryProfitGainSeriesData = expiryProfitGainSeriesData
        this.expiryProfitLossSeriesData = expiryProfitLossSeriesData
    }

    createOiSeriesData() {
        const oiBarsData: StackedBarsData[] = this.payoffStrikes.map((s) => ({
            time: s.strike as Time,
            values: [s.callOi, s.putOi]
        }))

        this.oiBarsData = oiBarsData
    }

    getNearestStrike(price: number) {
        if (!this.payoffStrikes.length) {
            return price
        }
        for (let i = 0; i < this.payoffStrikes.length; i++) {
            const payoffStrike = this.payoffStrikes[i]
            if (price === payoffStrike.strike) return price
            if (payoffStrike.strike < price) continue
            let previousStrike = this.payoffStrikes[i - 1]

            if (!previousStrike) return payoffStrike.strike

            if (price > (previousStrike.strike + payoffStrike.strike) / 2) {
                return payoffStrike.strike
            } else {
                return previousStrike.strike
            }
        }
        return this.payoffStrikes[this.payoffStrikes.length - 1].strike
    }

    onSliderMoveStart(legId: string) {
        if (!this.chartApi || !this.interactionsEnabled){
            return 
        } 
        this.dragStartX = this.payoffLegsPositions[legId] || 0
        this.draggingLegId = legId
    }

    onSliderMoveEnd(legId: string) {
        if (!this.chartApi) {
            this.dragStartX = 0
            this.draggingLegId = null
            return
        }
        if (!this.interactionsEnabled){
            store.dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Payoff chart interactions",
                group: Group.STRATEGY_BUILDER,
                featureRestrictionCode:FeatureCode.PAYOFF_CHART_INTERACTIONS
            }))
            return 
        } 
        this.dragStartX = 0
        this.draggingLegId = null
        const position = this.payoffLegsPositions[legId]

        const leftPriceScale = this.chartApi.priceScale('left')
        const leftPriceScaleVisible = leftPriceScale.options().visible
        const leftPriceScaleWidth = leftPriceScale.width()
        const time = this.chartApi.timeScale().coordinateToTime(position - (leftPriceScaleVisible ? leftPriceScaleWidth : 0))
        if (time) {
            const newStrike = this.getNearestStrike(time as number)
            const leg = this.payoffLegs.find((l) => l.id === legId)
            // Positions never relocate; dropping creates a new leg while the
            // existing position stays put, so we must not pin it to the drop
            // strike (which would otherwise leave the tag stuck there).
            if (leg?.type !== 'position') {
                this.pendingLegStrikes[legId] = newStrike
            }
            this.updatePayoffLegsPositions()
            this.createOrUpdatePayoffLegLines()
            this.callbacks.updateLeg(legId, newStrike)
        }
    }

    onSliderMove = (legId: string, deltaX: number) => {
        if (!this.chartApi || !this.interactionsEnabled){
            return 
        } 
        const position = this.dragStartX
        this.draggingLegId = legId
        const chartWidth = this.chartApi.timeScale().width()

        let newPosition = position + deltaX

        if (newPosition < 0) {
            newPosition = 0
        } else if (newPosition > chartWidth) {
            newPosition = chartWidth
        }

        this.payoffLegsPositions[legId] = newPosition

        const leg = this.payoffLegs.find((l) => l.id === legId)
        // For an existing position we keep the original tag pinned to its
        // strike (shown faded) and only let the preview line follow the cursor,
        // so the drag drops a brand new leg instead of relocating the position.
        if (leg?.type !== 'position' && this.orderSlidersElements[legId]) {
            this.orderSlidersElements[legId].style.left = `${newPosition}px`
        }
        const leftPriceScale = this.chartApi.priceScale('left')
        const leftPriceScaleVisible = leftPriceScale.options().visible
        const leftPriceScaleWidth = leftPriceScale.width()
        const time = this.chartApi.timeScale().coordinateToTime(newPosition - (leftPriceScaleVisible ? leftPriceScaleWidth : 0))
        if (time) {
            this.hoveredStrike = this.getNearestStrike(time as number)
        }
        this.createOrUpdatePayoffLegLines()
    }

    updateCallback(name: keyof PayoffChartCallbacks, callback: any) {
        this.callbacks[name] = callback
    }

    removeCallback(name: keyof PayoffChartCallbacks) {
        this.callbacks[name] = () => {}
    }

    unsubscribeEventListeners() {
        if (!this.chartApi) return
        this.chartApi.timeScale().unsubscribeVisibleTimeRangeChange(this.onVisibleTimeRangeChange)
        this.chartApi.unsubscribeCrosshairMove(this.onCrossHairMove)
        this.chartApi.unsubscribeClick(this.onClick)
    }

    destroy() {
        this.draggingLegId = null
        this.dragStartX = 0
        this.hoveredStrike = null
        this.unsubscribeEventListeners()
        this.sdLines.forEach((drawing) => {
            try {
                this.seriesApiMap[EXPIRY_PROFIT_SERIES].detachPrimitive(drawing)
                drawing.destroy()
            } catch (e) {}
        })
        this.sdLines = []

        Object.values(this.payoffLegLines).forEach((drawing) => {
            try {
                this.seriesApiMap[EXPIRY_PROFIT_SERIES].detachPrimitive(drawing)
                drawing.destroy()
            } catch (e) {}
        })

        this.payoffLegLines = {}

        this.seriesApiMap[EXPIRY_PROFIT_SERIES].detachPrimitive(this.plusIconDrawing)
        this.seriesApiMap = {}
        this.plusIconDrawing.destroy()
        this.plusIconDrawing = null
        this.chartApi.remove()
        this.chartApi = null
    }
}
